// src/components/dashboard/ai/flowsenseModules.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — the map of the ecosystem the dashboard AI explains.
// Pure data (no React). Used by the AI Command Center, the Modules widget, the
// dashboard helper drawer and the backend prompt (a trimmed copy is sent as context).
//
// ⚠ Routes: adjust `route` to your router if a path differs (e.g. the Hub route).
// ─────────────────────────────────────────────────────────────────────────────

/** AI / ML engines that power FlowSense. `healthKey` maps to useSystemHealth(). */
export const AI_ENGINES = Object.freeze({
    ml: {
        name: 'FlowSense ML service', short: 'ML', healthKey: 'mlService', color: 'purple',
        explain: 'Python service that runs the prediction models: asset remaining-useful-life (RUL / failure risk), technician dispatch, parts prediction, issue classifier (NLP triage), sensor anomaly detection (Isolation Forest) and flood risk.',
        powers: ['Assets at Risk', 'AI dispatch suggestions', 'Parts recommendations', 'Issue triage', 'Flood predictions'],
    },
    ollama: {
        name: 'Ollama language model', short: 'Ollama', healthKey: 'aiServer', color: 'pink',
        explain: 'Local large-language model. Writes technical briefs for work orders, drafts citizen updates and answers questions in this assistant using a live snapshot of the system.',
        powers: ['AI triage briefs', 'Citizen update drafts', 'FlowSense AI answers'],
    },
    whisper: {
        name: 'Whisper speech-to-text', short: 'Whisper', healthKey: 'whisperServer', color: 'cyan',
        explain: 'Turns technicians\' voice notes into text so they can be attached to work orders and read by the other models.',
        powers: ['Voice notes → text'],
    },
    websocket: {
        name: 'Live event stream (WebSocket)', short: 'Live', healthKey: null, color: 'green',
        explain: 'Pushes changes the moment they happen — work orders, sensor anomalies, AI predictions, SLA watchdog scans — so every page updates without reloading.',
        powers: ['Every live widget', 'Activity feed'],
    },
    backend: {
        name: 'FlowSense API', short: 'API', healthKey: 'backend', color: 'blue',
        explain: 'Node / Express API: authentication, work orders, assets, roads, inventory, SLA watchdog and the bridge to the ML service and Ollama.',
        powers: ['Everything'],
    },
    database: {
        name: 'MySQL database', short: 'DB', healthKey: 'database', color: 'gray',
        explain: 'System of record for assets, sensors, readings, work orders, inspections and history the models learn from.',
        powers: ['Everything'],
    },
});

/** FlowSense modules ("smart pages"). */
export const FLOWSENSE_MODULES = Object.freeze({
    dashboard: {
        name: 'Dashboard', route: null, color: 'blue', icon: 'dashboard',
        summary: 'The control room: live KPIs, alert map, assets at risk, SLA tracker, inventory and this AI assistant.',
        doHere: ['See what needs attention now', 'Ask FlowSense AI about the whole system', 'Customize widgets for your role'],
        aiModels: ['All engines (read-only overview)'],
        feeds: 'Nothing — it reads from every module.',
        liveGroups: ['workOrders', 'sla', 'alerts', 'predictions', 'issues', 'assets', 'roads', 'inventory', 'health'],
        ask: ['What needs my attention right now?', 'Give me a full breakdown of FlowSense'],
    },
    // --- UPDATE: Live Grid (geospatial command center — the Live Grid tab of the dashboard)
    live_grid: {
        name: 'Live Grid', route: null, color: 'purple', icon: 'map',
        summary: 'Geospatial command center: every mapped asset on CARTO / satellite / terrain basemaps with live status, telemetry pulses, sensor anomalies, ML failure risk, district zones and one-click AI work orders.',
        doHere: ['Find failing or at-risk assets on the map (AI Lens, ML-risk rings, live anomaly pulses)', 'Ask Grid AI what needs action and focus the asset', 'Generate a typed predictive / corrective work order with an Ollama brief', 'See zone health and active work orders per district'],
        aiModels: ['Asset RUL / failure risk (ML)', 'Prescriptive repair-or-replace', 'Ollama triage briefs + Grid AI', 'Anomaly detection (live)'],
        feeds: 'Work orders raised here link to the asset and update zone analytics.',
        liveGroups: ['assets', 'predictions', 'alerts', 'readings', 'workOrders'],
        ask: ['Explain the Live Grid', 'Which assets on the map need action?'],
    },
    // --- END UPDATE
    work_orders: {
        name: 'Work Orders', route: '/admin/work-orders', color: 'orange', icon: 'work',
        summary: 'Every job in FlowSense — repairs, emergencies, preventive work, inspections, paving / marking / striping, sensor and well work — with AI urgency, dispatch suggestions and SLA tracking.',
        doHere: ['Create a work order or inspection from any asset, road, well or sensor', 'Accept AI technician suggestions', 'Track SLAs and raise follow-ups from inspection findings'],
        aiModels: ['AI dispatcher', 'Parts recommender', 'SLA watchdog', 'Ollama (briefs, citizen updates)'],
        feeds: 'Completed jobs with notes teach the failure and parts models; links update asset / road / well history.',
        liveGroups: ['workOrders', 'sla'],
        ask: ['Which work orders are breaching SLA?', 'How do I create an inspection?'],
    },
    road_works: {
        name: 'Road Works', route: '/admin/road-works', color: 'yellow', icon: 'road',
        summary: 'The road network: segments, condition ratings, inspections and assessments, paving history, anomalies and road RUL predictions.',
        doHere: ['Record condition assessments and schedule inspections', 'Raise paving, pothole, marking and striping work', 'See roads at risk from flooding and degradation'],
        aiModels: ['Road RUL (survival model)', 'Anomaly clustering', 'Flood impact from the hydraulic model'],
        feeds: 'Assessments and completed road jobs update condition and the road degradation model.',
        liveGroups: ['roads', 'workOrders', 'predictions'],
        ask: ['Which roads are in the worst condition?', 'Explain the Road Works module'],
    },
    live_readings: {
        name: 'Live Readings', route: '/admin/live-readings', color: 'cyan', icon: 'sensor',
        summary: 'Real-time telemetry from IoT sensors and drainage wells — water levels, pressure, flow — with anomaly detection and flood alerts.',
        doHere: ['Watch live water levels and sensor health', 'Acknowledge anomalies and create sensor / well work orders', 'Check AI flood risk for wells'],
        aiModels: ['Isolation Forest anomaly detection', 'Flood risk model', 'Weather-aware hydrology'],
        feeds: 'Readings drive alerts, flood predictions and asset health scores.',
        liveGroups: ['readings', 'alerts', 'predictions'],
        ask: ['Are any wells at flood risk?', 'Explain Live Readings'],
    },
    public_utilities: {
        name: 'Public Utilities', route: '/admin/public-utilities', color: 'teal', icon: 'water',
        summary: 'The water network and utility assets — pumps, valves, mains, hydrants, meters, tanks — with status, failure reports and AI health.',
        doHere: ['Report an asset failure', 'See asset health and active work orders', 'Open an asset in the Digital Twin'],
        aiModels: ['Asset RUL / failure risk', 'Prescriptive repair-or-replace'],
        feeds: 'Failure reports and maintenance history train the failure model.',
        liveGroups: ['assets', 'workOrders', 'predictions'],
        ask: ['Which assets are most likely to fail?', 'Explain Public Utilities'],
    },
    digital_twin: {
        name: 'Digital Twin', route: '/admin/digital-twin', color: 'purple', icon: 'twin',
        summary: 'A virtual model of the network that replays telemetry and simulates scenarios (outages, pressure changes, failures).',
        doHere: ['Simulate what happens if an asset fails', 'See predicted failures on the network'],
        aiModels: ['Asset RUL', 'Simulation engine'],
        feeds: 'Scenario results guide preventive and predictive work.',
        liveGroups: ['predictions', 'assets'],
        ask: ['Explain the Digital Twin'],
    },
    flowsense_hub: {
        name: 'FlowSense Hub', route: '/admin/flowsense-hub', color: 'pink', icon: 'hub',
        summary: 'The central hub of FlowSense — the place to reach every module, integration and shared configuration.',
        doHere: ['Jump to any module', 'Review integrations and configuration'],
        aiModels: ['—'],
        feeds: 'Configuration used by the other modules.',
        liveGroups: [],
        ask: ['Explain the FlowSense Hub'],
    },
    helpdesk: {
        name: 'Helpdesk & Issues', route: '/helpdesk', color: 'red', icon: 'helpdesk',
        summary: 'Citizen and staff reported issues — triaged by the NLP classifier and converted into work orders; residents get updates when jobs complete.',
        doHere: ['Triage new issues', 'Convert an issue into a work order', 'Reply to residents'],
        aiModels: ['Issue classifier (NLP triage)', 'Ollama citizen updates'],
        feeds: 'Resolved tickets close the loop with residents and improve triage.',
        liveGroups: ['issues'],
        ask: ['How many issues are waiting for triage?'],
    },
    sla: {
        name: 'SLA Compliance', route: '/sla-compliance', color: 'red', icon: 'sla',
        summary: 'Service-level performance: compliance rate, at-risk and breached jobs, escalations — driven by the SLA watchdog.',
        doHere: ['See breached and at-risk jobs', 'Escalate or reassign'],
        aiModels: ['SLA watchdog'],
        feeds: 'Escalations notify supervisors and technicians.',
        liveGroups: ['sla', 'workOrders'],
        ask: ['What is our SLA compliance?'],
    },
    inventory: {
        name: 'Inventory & Procurement', route: '/admin/purchase-orders', color: 'green', icon: 'inventory',
        summary: 'Spare parts and materials: stock levels, low-stock alerts, valuation and purchase orders.',
        doHere: ['Reorder low-stock items', 'Check stock value'],
        aiModels: ['Parts recommender (what jobs will need)'],
        feeds: 'Parts used on work orders update stock and the parts model.',
        liveGroups: ['inventory'],
        ask: ['What stock is critically low?'],
    },
});

/** What each dashboard widget shows, where its data comes from and how to act on it. */
export const WIDGET_GUIDE = Object.freeze({
    ai: { name: 'FlowSense AI Command Center', shows: 'An AI brief of the whole system, the top priorities with one-click actions, live activity and a question box answered by Ollama (or built-in rules when Ollama is offline).', source: 'Work orders, alerts, issues, assets at risk, stock, SLA and engine health', act: 'Click a priority to jump to the module that fixes it, or ask a question.' },
    modules: { name: 'FlowSense Ecosystem', shows: 'Every FlowSense module, what it does, the AI behind it and how much live activity it has right now.', source: 'Live event stream', act: 'Open a module or ask the AI to explain it.' },
    stats: { name: 'Stat Cards', shows: 'Open work orders, unresolved issues and active sensor alerts with breakdowns.', source: 'Work orders, issues, active sensor alerts', act: 'Click a card for the full list.' },
    map: { name: 'Live Alert Map', shows: 'Active sensor alerts on the map, by severity, with heatmap and clustering.', source: 'Active sensor alerts', act: 'Open an alert to acknowledge it or create a work order.' },
    risk: { name: 'Assets at Risk (ML)', shows: 'Assets the failure model predicts are likely to fail, with risk scores and prescriptive actions.', source: 'ML service (RUL / failure risk)', act: 'Open an asset, confirm or deny the prediction, or create a predictive work order.' },
    workorders: { name: 'Work Orders Chart', shows: 'Created vs completed work orders per month.', source: 'Work orders', act: 'Change range / chart type; export.' },
    donut: { name: 'WO Status Donut', shows: 'How open and closed work is split by status right now.', source: 'Work orders', act: 'Click a slice to open Work Orders filtered by that status.' },
    issues: { name: 'Recent Issues', shows: 'The triage desk: unresolved citizen / staff issues by priority.', source: 'Issues', act: 'Open an issue and convert it into a work order.' },
    lowstock: { name: 'Low Stock Items', shows: 'Parts below their reorder threshold.', source: 'Inventory', act: 'Reorder to draft a purchase order.' },
    inventory: { name: 'Inventory Valuation', shows: 'Total value of current stock and top categories.', source: 'Inventory', act: 'View history.' },
    radial: { name: 'System Health', shows: 'Health of the API, database, ML service, Ollama and Whisper.', source: 'Health checks', act: 'If a service is down, the AI features that depend on it are limited.' },
    sla: { name: 'SLA Tracker', shows: 'SLA compliance, at-risk / breached counts and countdowns for the most urgent jobs.', source: 'SLA watchdog', act: 'Escalate or reassign a job from its menu.' },
});

export const MODULE_KEYS = Object.keys(FLOWSENSE_MODULES);
export const ENGINE_KEYS = Object.keys(AI_ENGINES);

/** Compact text description of FlowSense (sent to the backend as part of the AI prompt). */
export const describeEcosystem = () => [
    'FlowSense MOW+ modules:',
    ...MODULE_KEYS.map(k => `- ${FLOWSENSE_MODULES[k].name}: ${FLOWSENSE_MODULES[k].summary}`),
    'AI engines:',
    ...ENGINE_KEYS.map(k => `- ${AI_ENGINES[k].name}: ${AI_ENGINES[k].explain}`),
].join('\n');
